import React, { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";

const formatUsdTick = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "$0";

  if (Math.abs(num) >= 1000) {
    const short = (num / 1000).toFixed(0);
    return `$${short}k`;
  }

  return `$${num}`;
};

const formatUsdTooltip = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "$0";

  return `$${num.toLocaleString()}`;
};

const VisualInsights = ({ onError }) => {
  const { api } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const rows = await api("/api/analytics/insights");
        const normalized = Array.isArray(rows)
          ? rows.map((r) => ({
              symbol: r.symbol,
              totalPnl: r.totalPnl == null ? null : Number(r.totalPnl),
              avgPnl: r.avgPnl == null ? null : Number(r.avgPnl),
            }))
          : [];

        if (mounted) {
          setData(normalized);
          if (onError) onError(null);
        }
      } catch (err) {
        console.error("Error fetching insights:", err);
        if (mounted) {
          setData([]);
          if (onError) onError(err);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [api, onError]);

  if (loading) {
    return (
      <div className="rounded-xl border border-[#2a2a2a] bg-[#181818] p-5 text-center text-sm text-gray-400">
        Loading insights…
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="rounded-xl border border-[#2a2a2a] bg-[#181818] p-5 text-center text-sm text-gray-400">
        No data yet — add some trades to see your insights.
      </div>
    );
  }

  return (
    <div className="space-y-6 rounded-xl border border-[#2a2a2a]/40 bg-[rgba(18,18,18,0.75)] p-4 text-white shadow-[0_0_12px_rgba(0,0,0,0.4)] backdrop-blur-sm transition-all duration-300 sm:p-4 lg:p-5">
      <div>
        <div className="mb-2 text-center">
          <h2 className="text-base font-semibold md:text-lg">
            Total Profit / Loss by Symbol
          </h2>
          <p className="text-xs text-gray-500">PnL in USD</p>
        </div>

        <div className="w-full h-44 sm:h-48 lg:h-52">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 4, right: 8, left: -24, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#2c2c2c" />
              <XAxis dataKey="symbol" stroke="#aaa" tick={{ fontSize: 11 }} />
              <YAxis
                stroke="#aaa"
                tick={{ fontSize: 11 }}
                tickFormatter={formatUsdTick}
              />
              <Tooltip
                formatter={(value) => [formatUsdTooltip(value), "Total Profit / Loss"]}
                contentStyle={{
                  backgroundColor: "#1f1f1f",
                  border: "none",
                  borderRadius: "10px",
                }}
                labelStyle={{ color: "#fff" }}
                itemStyle={{ color: "#93c5fd" }}
              />
              <Bar dataKey="totalPnl" fill="#3b82f6" radius={[5, 5, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div>
        <div className="mb-2 text-center">
          <h2 className="text-base font-semibold md:text-lg">
            Average Profit / Loss by Symbol
          </h2>
          <p className="text-xs text-gray-500">Average PnL per trade in USD</p>
        </div>

        <div className="w-full h-44 sm:h-48 lg:h-52">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 4, right: 8, left: -24, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#2c2c2c" />
              <XAxis dataKey="symbol" stroke="#aaa" tick={{ fontSize: 11 }} />
              <YAxis
                stroke="#aaa"
                tick={{ fontSize: 11 }}
                tickFormatter={formatUsdTick}
              />
              <Tooltip
                formatter={(value) => [formatUsdTooltip(value), "Average Profit / Loss"]}
                contentStyle={{
                  backgroundColor: "#1f1f1f",
                  border: "none",
                  borderRadius: "10px",
                }}
                labelStyle={{ color: "#fff" }}
                itemStyle={{ color: "#facc15" }}
              />
              <Bar dataKey="avgPnl" fill="#facc15" radius={[5, 5, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default VisualInsights;